import { useToast } from '@citizen-walk/shared-ui';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { CurrentSessionState } from './useCurrentSession';

/**
 * user 第 6+8 弾 — URL path param `:sessionId` を現セッションに自動適用する hook。
 *
 * 旧 `/m/:districtToken/:sessionId` 互換 (旧 v1 では sessionStorage `pendingSessionId` 経由)。
 * AppHome / MapPage 共通で使う。route は `/m/:urlToken/:sessionId` および
 * `/m/:urlToken/:sessionId/map` で active。
 *
 * ref の境界:
 *   - 同一 component mount 内: 1 URL 1 回のみ適用 (picker 切替後の強制復元を回避)
 *   - 異なる URL に navigate: 新 sessionId で再適用
 *   - 同 URL を再 open / browser reload: component re-mount で ref がリセットされ再適用
 *
 * status='error': sessions subscribe 失敗時は ref を set して無限再評価を防ぎ、
 *   エラー文言は SessionPicker の既存 error 表示に委譲 (重複 Toast 回避)。
 *
 * 依存配列に `toast` を含むが、`useToast` の戻り値は ToastProvider 側で `useMemo` 安定化済
 * (packages/shared-ui/src/Toast.tsx)。再 render で参照が変わって useEffect が無駄に再実行される
 * 心配はない。
 */
export function useApplyUrlSessionId(
  state: CurrentSessionState,
  currentSessionId: string | null,
  setCurrentSessionId: (id: string | null) => void,
): void {
  const { sessionId: urlSessionId } = useParams();
  const appliedUrlSessionIdRef = useRef<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!urlSessionId) return;
    if (appliedUrlSessionIdRef.current === urlSessionId) return;
    if (state.status === 'loading') return;
    if (state.status === 'error') {
      appliedUrlSessionIdRef.current = urlSessionId;
      return;
    }
    const found = state.items.find((s) => s.id === urlSessionId);
    appliedUrlSessionIdRef.current = urlSessionId;
    if (!found) {
      toast('error', 'URL のセッションが見つかりません (削除済 / 別地区)');
      return;
    }
    if (currentSessionId === urlSessionId) return;
    setCurrentSessionId(urlSessionId);
    toast('success', `セッション「${found.name}」が選択されました`);
  }, [urlSessionId, state, currentSessionId, setCurrentSessionId, toast]);
}
